import { http } from './http';
import {
  Bilan,
  CandidateSearchResult,
  CreateBilanPayload,
  ExamForecastsResponse,
  InstructorLesson,
  LearningBooklet,
  LearningProgression,
  StartingEvaluation,
  PlanningResponse,
  Student,
  StudentDetail,
  StudentLesson,
  TodayResponse,
} from '../types';

export const instructorApi = {
  async getAccompaniments(from: string, to: string): Promise<{id:string;candidate_name:string;center:string;timezone:string;starts_at:string;ends_at:string}[]> {
    return (await http.get('/v1/exams/accompaniments/me', { params: {from,to} })).data;
  },
  async getToday(): Promise<TodayResponse> {
    const { data } = await http.get<TodayResponse>('/v1/instructor/me/today');
    return data;
  },
  async getPlanning(week?: string): Promise<PlanningResponse> {
    const { data } = await http.get<PlanningResponse>(
      '/v1/instructor/me/planning',
      { params: week ? { week } : undefined },
    );
    return data;
  },
  async getStudents(): Promise<Student[]> {
    const { data } = await http.get<Student[]>('/v1/instructor/me/students');
    return data;
  },
  async searchCandidates(q: string): Promise<CandidateSearchResult[]> {
    const { data } = await http.get<CandidateSearchResult[]>(
      '/v1/instructor/me/candidates-search',
      { params: q ? { q } : undefined },
    );
    return data;
  },
  async addStudent(candidateId: string): Promise<Student> {
    const { data } = await http.post<Student>(
      `/v1/instructor/me/students/${candidateId}`,
    );
    return data;
  },
  async removeStudent(candidateId: string): Promise<void> {
    await http.delete(`/v1/instructor/me/students/${candidateId}`);
  },
  async getExamForecasts(): Promise<ExamForecastsResponse> {
    const { data } = await http.get<ExamForecastsResponse>(
      '/v1/instructor/me/exam-forecasts',
    );
    return data;
  },
  /** Quinzaine où l'élève sera prêt : Permio en fait la demande de réservation. */
  async setExamForecast(
    candidateId: string,
    month: string,
    half: 1 | 2,
  ): Promise<ExamForecastsResponse> {
    const { data } = await http.put<ExamForecastsResponse>(
      `/v1/instructor/me/students/${candidateId}/exam-forecast`,
      { month, half },
    );
    return data;
  },
  async clearExamForecast(candidateId: string): Promise<ExamForecastsResponse> {
    const { data } = await http.delete<ExamForecastsResponse>(
      `/v1/instructor/me/students/${candidateId}/exam-forecast`,
    );
    return data;
  },
  async getStudentLearningBooklet(id: string): Promise<LearningBooklet> {
    const { data } = await http.get<LearningBooklet>(
      `/v1/instructor/me/students/${id}/learning-booklet`,
    );
    return data;
  },
  async setStudentLearningProgression(
    id: string,
    itemCode: string,
    progression: LearningProgression | null,
  ): Promise<LearningBooklet> {
    const { data } = await http.patch<LearningBooklet>(
      `/v1/instructor/me/students/${id}/learning-booklet/${itemCode}`,
      { progression },
    );
    return data;
  },
  async getStudentStartingEvaluation(id: string): Promise<StartingEvaluation> {
    const { data } = await http.get<StartingEvaluation>(
      `/v1/instructor/me/students/${id}/starting-evaluation`,
    );
    return data;
  },
  async setStudentStartingEvaluation(
    id: string,
    answers: Record<string, string>,
  ): Promise<StartingEvaluation> {
    const { data } = await http.patch<StartingEvaluation>(
      `/v1/instructor/me/students/${id}/starting-evaluation`,
      { answers },
    );
    return data;
  },
  async getStudent(id: string): Promise<StudentDetail> {
    const { data } = await http.get<StudentDetail>(
      `/v1/instructor/me/students/${id}`,
    );
    return data;
  },
  async createBilan(payload: CreateBilanPayload): Promise<Bilan> {
    const { data } = await http.post<Bilan>('/v1/instructor/me/bilans', payload);
    return data;
  },
  async cancelLesson(id: string): Promise<InstructorLesson> {
    const { data } = await http.post<InstructorLesson>(
      `/v1/instructor/me/lessons/${id}/cancel`,
    );
    return data;
  },
  async rescheduleLesson(
    id: string,
    payload: { startsAt: string; endsAt: string; notify?: boolean },
  ): Promise<InstructorLesson> {
    const { data } = await http.patch<InstructorLesson>(
      `/v1/instructor/me/lessons/${id}/reschedule`,
      payload,
    );
    return data;
  },
  async remindLesson(id: string): Promise<{ ok: boolean }> {
    const { data } = await http.post<{ ok: boolean }>(
      `/v1/instructor/me/lessons/${id}/remind`,
      {},
    );
    return data;
  },
  async getStudentLessons(id: string): Promise<StudentLesson[]> {
    const { data } = await http.get<StudentLesson[]>(
      `/v1/instructor/me/students/${id}/lessons`,
    );
    return data;
  },
  async signAttendance(
    id: string,
    signature: string,
    instructorSignature?: string,
  ): Promise<InstructorLesson> {
    const { data } = await http.post<InstructorLesson>(
      `/v1/instructor/me/lessons/${id}/attendance`,
      { signature, instructorSignature },
    );
    return data;
  },
  async downloadAttendanceSheet(from?: string, to?: string): Promise<Blob> {
    const response = await http.get('/v1/instructor/me/attendance-sheet', {
      params: { from, to },
      responseType: 'blob',
    });
    return response.data as Blob;
  },
};
